import { css } from "@emotion/css";
import { fontSize, fontWeight, space } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import { ActivityStatus } from "@octopusdeploy/octopus-server-client";
import type { ActivityElement, KubernetesObjectStatus, KubernetesStepStatus, TaskState } from "@octopusdeploy/octopus-server-client";
import { useMemo } from "react";
import * as React from "react";
import type { KubernetesDetailsFilter } from "~/areas/tasks/components/Task/Kubernetes/KubernetesDetailsLayout";
import { hasNoFilters, clusterScopedNamespaceFilter } from "~/areas/tasks/components/Task/Kubernetes/KubernetesDetailsLayout";
import { KubernetesStepSnapshotStatus } from "~/areas/tasks/components/Task/Kubernetes/Snapshot/KubernetesStepSnapshotStatus";
import { SnapshotTaken } from "~/areas/tasks/components/Task/Kubernetes/SnapshotTaken";
import type { StepWithKubernetesAction } from "~/areas/tasks/components/Task/Kubernetes/StepWithKubernetesAction";
import { TaskStatusIcon } from "~/areas/tasks/components/Task/Kubernetes/TaskStatusIcon";
import { FilterObjectCounter } from "~/areas/tasks/components/Task/Kubernetes/components/FilterObjectCounter";
import SimpleExpander from "~/components/SimpleExpander/index";
import TimeFromNowLabel from "~/components/TimeLabels/TimeFromNowLabel";
import StringHelper from "~/utils/StringHelper/index";
export type KubernetesSnapshotStepBaseProps = Pick<StepWithKubernetesAction, "isAtomicHelmStep" | "isResourceStatusEnabled"> & {
    stepActivity: Pick<ActivityElement, "Status" | "Ended" | "Started">;
    taskState?: TaskState;
    showObjectDrawer: (kos: KubernetesObjectStatus) => void;
};
type KubernetesSnapshotStepExpanderProps = KubernetesSnapshotStepBaseProps & {
    stepName: string;
    filter: KubernetesDetailsFilter;
    stepResourceStatus: KubernetesStepStatus | undefined;
};
const styles = {
    stepNameContainer: css({
        lineHeight: 1.4,
        fontSize: fontSize["base"],
    }),
    stepName: css({
        fontWeight: fontWeight["600"],
        paddingLeft: space[4],
    }),
    snapshotTakenContainer: css({
        paddingLeft: space[16],
        paddingRight: space[16],
        paddingTop: space[8],
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
    }),
};
const applyFilterToStatuses = (kos: KubernetesObjectStatus, filter: KubernetesDetailsFilter) => {
    let showItem = true;
    const checkActiveFilter = (filterValue: string | undefined, filterPredicate: (value: string) => boolean) => {
        //this filter is not active, don't do anything
        if (filterValue === undefined || StringHelper.isNullOrWhiteSpace(filterValue)) {
            return;
        }
        //if any active filter filters this out, run away!
        showItem &&= filterPredicate(filterValue);
    };
    checkActiveFilter(filter.targetName, (targetName) => targetName === kos.ClusterName);
    checkActiveFilter(filter.namespace, (namespace) => namespace === (kos.Namespace || clusterScopedNamespaceFilter));
    checkActiveFilter(filter.name, (name) => kos.Name.toLocaleLowerCase().includes(name.toLocaleLowerCase()));
    checkActiveFilter(filter.kind, (kind) => kind === kos.Kind);
    checkActiveFilter(filter.status, (status) => status === kos.Status);
    return showItem;
};
export const KubernetesSnapshotStepExpander = (props: KubernetesSnapshotStepExpanderProps) => {
    const { showObjectDrawer, stepResourceStatus, ...rest } = props;
    const lastUpdateMessage = React.useMemo(() => {
        if (props.stepActivity.Status === ActivityStatus.Canceled) {
            return <>This task was canceled prior to starting</>;
        }
        if (!props.stepResourceStatus) {
            return null;
        }
        if (props.stepActivity.Ended) {
            return <SnapshotTaken date={props.stepResourceStatus.LastUpdated}/>;
        }
        return (<>
                Last Updated: <TimeFromNowLabel time={props.stepResourceStatus.LastUpdated}/>
            </>);
    }, [props.stepActivity.Ended, props.stepActivity.Status, props.stepResourceStatus]);
    const filteredStatuses = useMemo(() => {
        if (!stepResourceStatus?.KubernetesObjects) {
            return;
        }
        //if there are no filters, just return the full array
        if (hasNoFilters(props.filter)) {
            return stepResourceStatus?.KubernetesObjects;
        }
        return stepResourceStatus?.KubernetesObjects.filter((kos) => applyFilterToStatuses(kos, props.filter));
    }, [props.filter, stepResourceStatus?.KubernetesObjects]);
    const objectStatusCounts = useMemo(() => ({ total: stepResourceStatus?.KubernetesObjects.length || 0, filteredTotal: filteredStatuses?.length || 0 }), [filteredStatuses?.length, stepResourceStatus?.KubernetesObjects.length]);
    return (<SimpleExpander key={props.stepName} accessibleName={props.stepName} title={<div className={styles.stepNameContainer}>
                    <TaskStatusIcon status={props.stepActivity.Status}/> <span className={styles.stepName}>{props.stepName}</span>
                </div>} errorKey={props.stepName} onDidExpand={(isExpanded) => logger.info("Expanded: {Expanded}", { Expanded: isExpanded })} isExpandedByDefault={true}>
            <div className={styles.snapshotTakenContainer}>
                <div aria-label="Last Updated">{lastUpdateMessage}</div>
                <FilterObjectCounter total={objectStatusCounts.total} filteredTotal={objectStatusCounts.filteredTotal} objectLabel={"objects"}/>
            </div>
            <KubernetesStepSnapshotStatus showObjectDrawer={showObjectDrawer} stepObjectStatuses={filteredStatuses} {...rest}/>
        </SimpleExpander>);
};
